'use client';

import {
    AirportShuttleRounded,
    CarRentalRounded,
    CategoryRounded,
    ChevronRightRounded,
    DirectionsBoatRounded,
    FlightRounded,
    HotelRounded,
    LocalActivityRounded,
    RestaurantRounded,
} from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    Container,
    Divider,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { Flag } from '@odys/design-system/components/Flag';
import { GlassmorphicCta } from '@odys/design-system/components/GlassmorphicCta';
import { elevation } from '@odys/design-system/tokens/elevation';
import { radius } from '@odys/design-system/tokens/radius';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';

import type { DmcCategoryCountsDto, DmcPageDto } from '@/app/api/lib/dmcs/dmc.types';
import type { ItineraryCardDto } from '@/app/api/lib/itineraries/itinerary.types';
import type { Role } from '@/app/api/lib/permissions/matrix';
import {
    PRODUCT_CATEGORIES,
    type ProductCategory,
} from '@/app/api/lib/products/product.vocabulary';
import { AccessGateScreen } from '@/components/common/AccessGateScreen';
import { SurfaceCard } from '@odys/design-system/components/SurfaceCard';
import { CERTIFICATION_LOGOS } from '@/components/dmcs/certificationLogos';
import { PresentationMediaCarousel } from '@/components/dmcs/PresentationMediaCarousel';
import { ItineraryCard } from '@/components/itineraries/ItineraryCard';
import { useApiFetch } from '@/configurations/api/useApiFetch';
import { Link } from '@/configurations/i18n/navigation';

import type { ReactNode } from 'react';

type DmcPageProps = {
    slug: string;
    viewerRole: Role;
};

class DmcNotFound extends Error {}

type CategoryKey = ProductCategory;
const CATEGORY_KEYS: readonly CategoryKey[] = PRODUCT_CATEGORIES;

const CATEGORY_ICONS: Record<CategoryKey, typeof HotelRounded> = {
    hotels: HotelRounded,
    activities: LocalActivityRounded,
    cruises: DirectionsBoatRounded,
    transports: AirportShuttleRounded,
    internationalFlight: FlightRounded,
    rentalCars: CarRentalRounded,
    restaurants: RestaurantRounded,
    general: CategoryRounded,
};

export function DmcPage({ slug, viewerRole }: DmcPageProps) {
    const t = useTranslations('dmcPage');
    const locale = useLocale();
    const apiFetch = useApiFetch();

    const dmcQuery = useQuery<DmcPageDto, Error>({
        queryKey: ['dmc-page', locale, slug],
        queryFn: async () => {
            const res = await apiFetch(`/api/dmcs/${slug}`);
            if (res.status === 404) throw new DmcNotFound();
            if (!res.ok) throw new Error(`DMC fetch failed (${res.status})`);
            return ((await res.json()) as { data: DmcPageDto }).data;
        },
        retry: (failureCount, error) => (error instanceof DmcNotFound ? false : failureCount < 2),
    });

    if (dmcQuery.error instanceof DmcNotFound) return <NotFoundScreen />;
    if (dmcQuery.isPending) return <DmcPageSkeleton />;
    if (dmcQuery.error) {
        return (
            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Alert severity="error">{t('error')}</Alert>
            </Container>
        );
    }

    const dmc = dmcQuery.data;

    return (
        <Container maxWidth="lg" sx={{ pt: 4, pb: 7 }}>
            <Stack spacing={4}>
                {dmc.isOwnPage && !dmc.isPublished && <UnpublishedBanner />}
                <HeadSection dmc={dmc} viewerRole={viewerRole} />
                <PresentationSection dmc={dmc} />
                {dmc.itineraries.length > 0 && (
                    <ItinerariesSection
                        dmcSlug={dmc.identity.slug}
                        itineraries={dmc.itineraries}
                        viewerRole={viewerRole}
                    />
                )}
                <CategoryCardsSection counts={dmc.categoryCounts} dmcSlug={dmc.identity.slug} />
                <CertificationsSection certifications={dmc.certifications} />
                {viewerRole === 'visitor' && (
                    <VisitorCta backgroundImageUrl={dmc.identity.heroImageUrl} />
                )}
            </Stack>
        </Container>
    );
}

/**
 * Bannière « catalogue non publié » : affichée uniquement au propriétaire d'une fiche
 * `is_active = false` qui la consulte en aperçu (`isOwnPage && !isPublished`, #2492). La fiche
 * reste consultable, mais n'est visible que de lui — un CTA email pointe vers l'équipe pour la
 * publier. Jamais affichée à une autre DMC, une Agency ou un Visiteur (garde `isOwnPage`).
 */
function UnpublishedBanner() {
    const t = useTranslations('dmcPage');

    return (
        <Alert severity="info" sx={{ borderRadius: radius.md }}>
            <Typography variant="subtitle2" component="p">
                {t('unpublishedTitle')}
            </Typography>
            <Typography variant="body2" component="p">
                {t('unpublishedBody')}
            </Typography>
            <Box
                component="a"
                href="mailto:hello@odys.travel"
                sx={{ color: 'inherit', fontWeight: 600 }}
            >
                hello@odys.travel
            </Box>
        </Alert>
    );
}

/**
 * Bloc head : breadcrumb (3 niveaux) → hero image + logo overlay →
 * titre h3 + 2 boutons (Agency) / aucun (DMC, Visitor) +
 * ligne meta "{n} Destination · Tag · …".
 */
function HeadSection({ dmc, viewerRole }: { dmc: DmcPageDto; viewerRole: Role }) {
    const t = useTranslations('dmcPage');
    const firstDestination = dmc.presentation.destinations[0];

    return (
        <Stack spacing={3}>
            <Breadcrumbs aria-label="breadcrumb">
                <Box
                    component={Link}
                    href="/"
                    sx={{ color: 'text.secondary', textDecoration: 'none' }}
                >
                    {t('breadcrumbHome')}
                </Box>
                {firstDestination && (
                    <Box
                        component={Link}
                        href={`/destinations/${firstDestination.slug}`}
                        sx={{ color: 'text.secondary', textDecoration: 'none' }}
                    >
                        {firstDestination.name}
                    </Box>
                )}
                <Typography
                    sx={{
                        color: 'text.primary',
                    }}
                >
                    {dmc.identity.name}
                </Typography>
            </Breadcrumbs>

            <Hero dmc={dmc} />

            <TitleBlock dmc={dmc} viewerRole={viewerRole} />
        </Stack>
    );
}

function Hero({ dmc }: { dmc: DmcPageDto }) {
    const cover = dmc.identity.heroImageUrl;
    const logo = dmc.identity.logoUrl;

    return (
        <Box
            data-testid="dmc-hero"
            sx={{
                position: 'relative',
                width: '100%',
                height: 360,
                borderRadius: radius.lg,
                overflow: 'visible',
            }}
        >
            <Box
                data-testid="dmc-hero-cover"
                data-cover={cover ? 'present' : 'placeholder'}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    borderRadius: radius.lg,
                    overflow: 'hidden',
                    bgcolor: 'grey.300',
                }}
            >
                {cover && (
                    <Image
                        src={cover}
                        alt=""
                        fill
                        priority
                        sizes="(max-width: 960px) 100vw, 960px"
                        style={{ objectFit: 'cover' }}
                    />
                )}
            </Box>
            <Box
                sx={{
                    position: 'absolute',
                    left: 23,
                    top: 265,
                    p: 1,
                    bgcolor: 'background.default',
                    borderRadius: radius.lg,
                }}
            >
                <Avatar
                    src={logo ?? undefined}
                    alt={dmc.identity.name}
                    variant="rounded"
                    data-testid="dmc-hero-logo"
                    data-logo={logo ? 'present' : 'placeholder'}
                    sx={{
                        width: 140,
                        height: 140,
                        borderRadius: radius.md,
                        bgcolor: 'common.white',
                        color: 'text.primary',
                        fontSize: 36,
                        fontWeight: 600,
                        boxShadow: elevation.cardHover,
                        '& img': { objectFit: 'contain' },
                    }}
                >
                    {dmc.identity.name.charAt(0)}
                </Avatar>
            </Box>
        </Box>
    );
}

function TitleBlock({ dmc, viewerRole }: { dmc: DmcPageDto; viewerRole: Role }) {
    const t = useTranslations('dmcPage');
    const metaParts: string[] = [t('counters.destinations', { count: dmc.counts.destinations })];
    if (dmc.presentation.specialties[0]) {
        metaParts.push(dmc.presentation.specialties[0].label);
    }

    return (
        // pt: 7 = ~56px pour que le titre passe sous le logo overlay (140px
        // de hauteur + 8px padding) qui dépasse du hero de ~53px (top=265,
        // hero height=360). Sans ce padding-top, le titre est masqué.
        <Stack spacing={1} sx={{ pt: 7, pb: 2 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                        color: 'text.primary',
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {dmc.identity.name}
                </Typography>
                <CtaStrip viewerRole={viewerRole} dmc={dmc} />
            </Stack>
            <Stack
                direction="row"
                spacing={1}
                divider={<Divider orientation="vertical" flexItem />}
                useFlexGap
                sx={{
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    color: 'text.secondary',
                }}
            >
                {metaParts.map((part, idx) => (
                    <Typography
                        key={`${part}-${idx}`}
                        component="span"
                        sx={{
                            fontWeight: 600,
                            letterSpacing: '0.15px',
                            color: 'inherit',
                        }}
                    >
                        {part}
                    </Typography>
                ))}
            </Stack>
        </Stack>
    );
}

/**
 * Accès aux deux formulaires de la fiche. Ouvert aux Visiteurs depuis
 * l'issue #2429 — sans ces CTA ils n'auraient aucun chemin vers des
 * formulaires désormais autorisés.
 *
 * Un DMC reste exclu, comme dans `route-policy` (`denial: { dmc:
 * 'access-denied' }`) : il traite ses demandes reçues dans Odys. Le test porte
 * bien sur `'dmc'` et non sur `!== 'agency'` — `viewerRole` est ici le rôle de
 * SESSION, pas le rôle downgradé par `viewerRoleFor`, donc un DMC qui consulte
 * une autre fiche vaut toujours `'dmc'` ici.
 *
 * Les `data-testid` et clés i18n gardent leur préfixe `agency`, historique :
 * les renommer toucherait six fichiers de locales et les tests existants pour
 * un gain cosmétique.
 */
function CtaStrip({ viewerRole, dmc }: { viewerRole: Role; dmc: DmcPageDto }) {
    const t = useTranslations('dmcPage');

    if (viewerRole === 'dmc') return null;

    return (
        <Stack direction="row" spacing={1.5} data-testid="dmc-agency-cta">
            <Button
                variant="outlined"
                component={Link}
                href={`/dmcs/${dmc.identity.slug}/contact`}
                data-testid="dmc-agency-contact-cta"
            >
                {t('agencyCta.contact')}
            </Button>
            <Button
                variant="contained"
                component={Link}
                href={`/dmcs/${dmc.identity.slug}/brief`}
                data-testid="dmc-agency-brief-cta"
            >
                {t('agencyCta.brief')}
            </Button>
        </Stack>
    );
}

/**
 * Section À propos : 2 colonnes desktop (texte + chips à gauche, video/image
 * à droite). Le titre h5 inclut le nom de la DMC.
 *
 * Quand le DMC consulte SA propre fiche (`isOwnPage`), chaque bloc vide affiche
 * un empty state owner-only (incitation à compléter), au lieu d'être masqué.
 * Pour Agency / Visiteur / DMC sur une autre fiche, les blocs vides restent
 * masqués et la grille se reflow (cf. `catalog-dmc-own-page-empty-states`).
 */
function PresentationSection({ dmc }: { dmc: DmcPageDto }) {
    const t = useTranslations('dmcPage');
    const tPres = useTranslations('dmcPage.presentation');
    const tEmpty = useTranslations('dmcPage.presentation.emptyStates');
    const { isOwnPage } = dmc;
    const { longDescription, specialties, targetMarket, destinations, media } = dmc.presentation;
    const hasMedia = media.length > 0;

    const hasText =
        Boolean(longDescription) ||
        specialties.length > 0 ||
        targetMarket.length > 0 ||
        destinations.length > 0;

    if (!isOwnPage && !hasText && !hasMedia) return null;

    const showMediaColumn = hasMedia || isOwnPage;

    return (
        <Box
            data-testid="dmc-presentation-section"
            sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: showMediaColumn ? '1fr 1fr' : '1fr' },
                alignItems: 'center',
            }}
        >
            <Stack spacing={2}>
                <Typography variant="h3" component="h2" sx={{ color: 'text.primary' }}>
                    {t('aboutTitle', { name: dmc.identity.name })}
                </Typography>
                {longDescription ? (
                    <Typography
                        variant="body1"
                        data-testid="presentation-text"
                        sx={{
                            fontWeight: 400,
                            letterSpacing: '0.15px',
                            lineHeight: 1.5,
                            color: 'text.primary',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        {longDescription}
                    </Typography>
                ) : (
                    isOwnPage && (
                        <PresentationEmptyState testId="presentation-empty-description">
                            {tEmpty('description')}
                        </PresentationEmptyState>
                    )
                )}
                {destinations.length > 0 ? (
                    <Box data-testid="presentation-destinations">
                        <SectionLabel>{tPres('destinationsTitle')}</SectionLabel>
                        <Stack
                            direction="row"
                            useFlexGap
                            sx={{
                                flexWrap: 'wrap',
                                rowGap: 1,
                            }}
                        >
                            {destinations.map((d) => (
                                <Chip
                                    key={d.slug}
                                    label={
                                        <Stack
                                            direction="row"
                                            spacing={0.75}
                                            component="span"
                                            sx={{
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Flag
                                                countryIso={d.countryIso}
                                                sx={{ width: 16, height: 12, display: 'block' }}
                                            />
                                            <span>{d.name}</span>
                                        </Stack>
                                    }
                                    size="small"
                                    variant="filled"
                                />
                            ))}
                        </Stack>
                    </Box>
                ) : (
                    isOwnPage && (
                        <PresentationEmptyState testId="presentation-empty-destinations">
                            {tEmpty('destinations')}
                        </PresentationEmptyState>
                    )
                )}
                {targetMarket.length > 0 ? (
                    <Box data-testid="presentation-markets">
                        <SectionLabel>{tPres('targetMarketsTitle')}</SectionLabel>
                        <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            sx={{
                                flexWrap: 'wrap',
                            }}
                        >
                            {targetMarket.map((market) => (
                                <Chip key={market} label={market} size="small" variant="filled" />
                            ))}
                        </Stack>
                    </Box>
                ) : (
                    isOwnPage && (
                        <PresentationEmptyState testId="presentation-empty-markets">
                            {tEmpty('markets')}
                        </PresentationEmptyState>
                    )
                )}
                {specialties.length > 0 ? (
                    <Box data-testid="presentation-specialties">
                        <SectionLabel>{tPres('specialtiesTitle')}</SectionLabel>
                        <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            sx={{
                                flexWrap: 'wrap',
                            }}
                        >
                            {specialties.map((s) => (
                                <Chip key={s.slug} label={s.label} size="small" variant="filled" />
                            ))}
                        </Stack>
                    </Box>
                ) : (
                    isOwnPage && (
                        <PresentationEmptyState testId="presentation-empty-specialties">
                            {tEmpty('specialties')}
                        </PresentationEmptyState>
                    )
                )}
            </Stack>
            {hasMedia && <PresentationMediaCarousel media={media} />}
            {!hasMedia && isOwnPage && (
                <PresentationEmptyState testId="presentation-empty-media">
                    {tEmpty('media')}
                </PresentationEmptyState>
            )}
        </Box>
    );
}

/**
 * Encart d'alerte owner-only signalant un bloc Présentation vide. Texte seul,
 * aucune action (pas de bouton d'édition) — palette `warning` du thème.
 */
function PresentationEmptyState({ children, testId }: { children: ReactNode; testId: string }) {
    return (
        <Alert severity="warning" icon={false} data-testid={testId} sx={{ py: 0.5 }}>
            {children}
        </Alert>
    );
}

function SectionLabel({ children }: { children: ReactNode }) {
    return (
        <Typography
            sx={{
                display: 'block',
                fontWeight: 600,
                letterSpacing: '0.15px',
                color: 'text.secondary',
                mb: 0.5,
            }}
        >
            {children}
        </Typography>
    );
}

function ItinerariesSection({
    dmcSlug,
    itineraries,
    viewerRole,
}: {
    dmcSlug: string;
    itineraries: ReadonlyArray<ItineraryCardDto>;
    viewerRole: Role;
}) {
    const t = useTranslations('dmcPage.itinerariesSection');
    const visible = itineraries.slice(0, 3);

    return (
        <Box data-testid="dmc-itineraries-section">
            <Stack
                direction="row"
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <Typography variant="h3" component="h2" sx={{ color: 'text.primary' }}>
                    {t('title')}
                </Typography>
                <Button
                    component={Link}
                    href={`/dmcs/${dmcSlug}/itineraries`}
                    variant="outlined"
                    size="small"
                    endIcon={<ChevronRightRounded />}
                >
                    {t('viewAll')}
                </Button>
            </Stack>
            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        md: 'repeat(3, minmax(0, 1fr))',
                    },
                }}
            >
                {visible.map((itin) => (
                    <ItineraryCard
                        key={itin.externalId}
                        item={itin}
                        viewerRole={viewerRole}
                        testId={`dmc-itinerary-card-${itin.externalId}`}
                    />
                ))}
            </Box>
        </Box>
    );
}

function CategoryCardsSection({
    counts,
    dmcSlug,
}: {
    counts: DmcCategoryCountsDto;
    dmcSlug: string;
}) {
    const t = useTranslations('dmcPage');
    const visible = CATEGORY_KEYS.filter((key) => counts[key] > 0);
    if (visible.length === 0) return null;

    return (
        <Box data-testid="dmc-category-cards-section">
            <Typography variant="h3" component="h2" sx={{ mb: 2, color: 'text.primary' }}>
                {t('categoriesSection.title')}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                        xs: '1fr 1fr',
                        sm: 'repeat(2, 1fr)',
                        md: `repeat(${Math.min(visible.length, 4)}, 1fr)`,
                    },
                }}
            >
                {visible.map((key) => (
                    <CategoryCard
                        key={key}
                        categoryKey={key}
                        count={counts[key]}
                        dmcSlug={dmcSlug}
                    />
                ))}
            </Box>
        </Box>
    );
}

function CategoryCard({
    categoryKey,
    count,
    dmcSlug,
}: {
    categoryKey: CategoryKey;
    count: number;
    dmcSlug: string;
}) {
    const t = useTranslations('dmcPage');
    const Icon = CATEGORY_ICONS[categoryKey];
    const label = t(`categoriesSection.labels.${categoryKey}`);
    // Espace insécable avant la flèche : sur les libellés longs (ex. "Vols
    // internationaux", "Internationale Flüge"), le texte peut passer sur 2 lignes
    // mais la flèche reste collée au dernier mot au lieu de tomber seule en dessous.
    const linkText = t('categoriesSection.linkTemplate', { category: label }).replace(
        /\s*→\s*$/u,
        ' →',
    );

    return (
        <SurfaceCard
            href={`/dmcs/${dmcSlug}/products/${categoryKey}`}
            LinkComponent={Link}
            data-testid={`dmc-category-card-${categoryKey}`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 2.5,
            }}
        >
            <Icon
                data-testid={`dmc-category-icon-${categoryKey}`}
                sx={{ fontSize: 32, color: 'primary.main' }}
            />
            <Typography variant="h4" component="h3" sx={{ mt: 0.5 }}>
                {label}
            </Typography>
            <Typography
                sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                    lineHeight: 1.6,
                }}
            >
                {t('categoriesSection.productsCount', { count })}
            </Typography>
            <Typography
                sx={{
                    mt: 1,
                    fontWeight: 500,
                    color: 'primary.main',
                }}
            >
                {linkText}
            </Typography>
        </SurfaceCard>
    );
}

/**
 * Section Certifications (#2116) — bande pleine largeur, fond dégradé fourni par
 * le design (radial + voile blanc 60 %). Contenu centré max-width 960, logos à
 * gauche / titre + description à droite. Masquée quand aucun logo n'est actif ;
 * titre/description omis individuellement s'ils sont vides.
 */
function CertificationsSection({
    certifications,
}: {
    certifications: DmcPageDto['certifications'];
}) {
    if (certifications.logos.length === 0) return null;

    return (
        <Box
            data-testid="dmc-certifications-section"
            sx={{
                // Full-bleed : sort du Container pour couvrir toute la largeur du viewport.
                width: '100vw',
                position: 'relative',
                left: '50%',
                transform: 'translateX(-50%)',
                py: { xs: 6, md: 10 },
                // Dégradé décoratif exporté de Figma, à quatre arrêts pastel. Aucune
                // autre surface ne l'emploie et aucun cran de la palette ne s'en
                // approche : le tokeniser inventerait une couleur de marque là où il
                // n'y a qu'une illustration. Seule exception assumée de la garde ici.
                background:
                    // eslint-disable-next-line odys/no-hardcoded-color -- dégradé décoratif unique, cf. ci-dessus
                    'radial-gradient(232.27% 141.42% at 0% 100.09%, #D5F7F8 12.98%, #E5F9E9 19.71%, #F8F5EF 29.33%, #F5F0EB 38.94%), linear-gradient(0deg, rgba(255, 255, 255, 0.60) 0%, rgba(255, 255, 255, 0.60) 100%)',
            }}
        >
            <Box
                sx={{
                    maxWidth: 960,
                    mx: 'auto',
                    px: 3,
                    display: 'grid',
                    gap: { xs: 4, md: 3 },
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    alignItems: 'center',
                }}
            >
                <Stack
                    direction="row"
                    useFlexGap
                    sx={{
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 3,
                    }}
                >
                    {certifications.logos.map((key) => {
                        const logo = CERTIFICATION_LOGOS[key];
                        return (
                            <Image
                                key={key}
                                src={logo.src}
                                alt={logo.alt}
                                width={logo.width}
                                height={logo.height}
                                data-testid={`certification-logo-${key}`}
                                style={{
                                    width: logo.displayWidth ?? 'auto',
                                    height: 'auto',
                                    maxWidth: logo.displayMaxWidth,
                                    maxHeight: logo.displayMaxHeight,
                                    objectFit: 'contain',
                                }}
                            />
                        );
                    })}
                </Stack>
                <Stack spacing={2}>
                    {certifications.title && (
                        <Typography
                            variant="h3"
                            component="h2"
                            data-testid="certifications-title"
                            sx={{ color: 'text.primary' }}
                        >
                            {certifications.title}
                        </Typography>
                    )}
                    {certifications.description && (
                        <Typography
                            variant="body2"
                            data-testid="certifications-description"
                            sx={{
                                color: 'text.primary',
                                letterSpacing: '0.15px',
                                lineHeight: 1.5,
                                whiteSpace: 'pre-line',
                            }}
                        >
                            {certifications.description}
                        </Typography>
                    )}
                </Stack>
            </Box>
        </Box>
    );
}

/**
 * CTA glassmorphique en pied de page pour les Visitor — image de la DMC
 * en fond + backdrop-blur, texte blanc, bouton teal. Remplace l'ancien
 * bandeau sticky.
 */
function VisitorCta({ backgroundImageUrl }: { backgroundImageUrl: string | null }) {
    const t = useTranslations('dmcPage.visitorCta');
    return (
        <GlassmorphicCta
            data-testid="dmc-visitor-cta"
            heading={t('heading')}
            message={t('message')}
            backgroundImageUrl={backgroundImageUrl}
            action={
                <Button component={Link} href="/sign-up" variant="contained">
                    {t('button')}
                </Button>
            }
        />
    );
}

function DmcPageSkeleton() {
    return (
        <Container maxWidth="lg" sx={{ pt: 4, pb: 7 }}>
            <Stack spacing={4}>
                <Skeleton variant="text" width={240} />
                <Skeleton variant="rectangular" height={360} sx={{ borderRadius: radius.lg }} />
                <Skeleton variant="text" width="60%" height={56} />
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: radius.lg }} />
            </Stack>
        </Container>
    );
}

function NotFoundScreen() {
    const t = useTranslations('dmcPage.notFound');
    return (
        <AccessGateScreen
            data-testid="dmc-not-found"
            title={t('heading')}
            body={t('message')}
            actions={
                <Button component={Link} href="/" variant="contained" size="large">
                    {t('backHome')}
                </Button>
            }
        />
    );
}
